/**
 * The upload allowlist is image/audio/video, and the server derives the stored extension
 * from the content type rather than the filename. So a file whose type the picker left
 * empty cannot be uploaded — it would be refused by the allowlist after the round trip.
 *
 * Inferring from the extension recovers the common case (iOS share sheets and some
 * Android pickers hand over an empty `type`), and anything still unknown is refused HERE,
 * with a sentence, rather than being sent to be rejected.
 */
const BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  m4a: "audio/mp4",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  aac: "audio/aac",
  caf: "audio/x-caf",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};

const ALLOWED = /^(image|audio|video)\//;

/** The content type to declare, or null when we cannot honestly name one. */
export function resolveContentType(
  contentType: string | undefined | null,
  fileName: string | undefined | null,
): string | null {
  const given = (contentType ?? "").trim().toLowerCase();
  // `application/octet-stream` is what a picker says when it means "I don't know".
  if (given && given !== "application/octet-stream" && ALLOWED.test(given))
    return given;

  const ext = (fileName ?? "").toLowerCase().split(".").pop() ?? "";
  const guessed = BY_EXTENSION[ext];
  return guessed ?? null;
}

/** What to tell someone whose file we will not send. */
export const UNSUPPORTED_FILE_MESSAGE =
  "That file type is not supported — photos, audio and video only.";
